<template>
  <div>
    <v-card flat>
      <v-card-text>
        <v-row align="center">
          <v-col cols="auto" class="d-flex align-center">
            <dropdown
              :label="$t('order_status')"
              :items="statusItems"
              v-model="orderStatus"
              hide-details
            ></dropdown>
          </v-col>
          <v-col cols="auto" class="d-flex align-center">
            <shift-picker v-model="selectedShifts" multiple />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row justify="center" class="py-15" no-gutters v-if="loading">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>
    <template v-else>
      <div class="mt-6" v-for="company in companies">
        <h2>{{ company.name }}</h2>
        <v-data-table
          :headers="headers"
          :items="customSearch(company.users, headers, search)"
          :loading="loading"
          class="striped-table ordera-box-shadow"
          hide-default-footer
          disable-pagination
          dense
        >
          <template v-slot:no-data>
            <v-container class="text-center py-12">
              <v-icon size="64" color="grey lighten-1" class="mb-4">
                mdi-account-off
              </v-icon>
              <div class="text-h6 grey--text text--darken-1 mb-2">
                {{ $t('no_employees_report_data') }}
              </div>
              <div class="text-body-2 grey--text">
                {{ $t('select_date_and_filters') }}
              </div>
            </v-container>
          </template>
          <template v-slot:item.num="{ item, index }">
            {{ index + 1 }}
          </template>
          <template v-slot:item.actions="{ item }">
            <circle-button
              @click="openDialogReportUser(item)"
              color="primary"
              icon="mdi-format-list-bulleted"
              :tooltip="$t('tooltip_order_details')"
            ></circle-button>
          </template>
          <template v-slot:body.append v-if="orderStatus !== 2">
            <tr class="font-weight-bold">
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>{{ $t("total") }}</td>
              <td v-if="selectedShifts.includes(1)">{{ company.totals.shift1 }}</td>
              <td v-if="selectedShifts.includes(2)">{{ company.totals.shift2 }}</td>
              <td v-if="selectedShifts.includes(3)">{{ company.totals.shift3 }}</td>
              <td v-if="selectedShifts.includes(4)">{{ company.totals.shift4 }}</td>
              <td>{{ company.totals.total }}</td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
      </div>
      <company-report-dialog-employee v-model="employeeDialog" :employee="selectedUser">
      </company-report-dialog-employee>
    </template>
  </div>
</template>

<script>
import reportService from "@/services/report";
import CompanyReportDialogEmployee from "@/components/company/reports/companyTabs/dialogs/companyEmployeeOrdersDialog.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";

export default {
  name: "CompanyReportTabMeals",
  mixins: [hasPrinting, hasExcel, hasReportPrinting],
  components: {
    CompanyReportDialogEmployee,
  },
  data() {
    return {
      report: null,
      loading: false,
      selectedUser: {},
      employeeDialog: false,
      orderStatus: 0, // Default: all users
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
    };
  },
  created() {
    this.fetchUserlist();
  },
  methods: {
    fetchUserlist() {
      this.loading = true;
      reportService
        .getCompanyUserlist({
          company_id: this.selectedCompanies,
          date: this.dates.from,
          date2: this.dates.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .catch((error) => {
          console.error("Error fetching meals:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openDialogReportUser(item, food) {
      this.selectedUser = item;
      this.employeeDialog = true;
    },
  },
  computed: {
    statusItems() {
      return [
        { text: this.$t("all"), value: 0 },
        { text: this.$t("users_ordered"), value: 1 },
        { text: this.$t("not_ordered"), value: 2 },
      ];
    },
    companies() {
      if (!this.report) {
        return [];
      }

      return Object.entries(this.report).map(([companyName, companyData]) => {
        const userMap = new Map();
        const companyTotals = {
          shift1: 0,
          shift2: 0,
          shift3: 0,
          shift4: 0,
          total: 0,
        };

        // First, add all users from the company (both who ordered and who didn't)
        if (companyData.users) {
          companyData.users
            .filter((user) => user.id && user.id !== null && user.id !== undefined)
            .forEach((user) => {
              userMap.set(user.id, {
                id: user.id,
                name: this.toLatin(user.name),
                lastname: this.toLatin(user.lastname),
                external_code: user.external_code,
                shift1: { quantity: 0 },
                shift2: { quantity: 0 },
                shift3: { quantity: 0 },
                shift4: { quantity: 0 },
                total: 0,
                order_count: 0,
                order_payed: 0,
              });
            });
        }

        // Then, update users with meal data
        (companyData.meals || [])
          .filter((meal) => meal.total > 0 && meal.id)
          .forEach((meal) => {
            Object.entries(meal.users || {}).forEach(([shiftKey, usersInShift]) => {
              (usersInShift || [])
                .filter((user) => user.id && user.id !== null && user.id !== undefined)
                .forEach((user) => {
                  let userRecord = userMap.get(user.id);
                  if (!userRecord) {
                    // If user not in company list, create them
                    userRecord = {
                      id: user.id,
                      name: user.name,
                      lastname: user.lastname,
                      external_code: user.external_code,
                      shift1: { quantity: 0 },
                      shift2: { quantity: 0 },
                      shift3: { quantity: 0 },
                      shift4: { quantity: 0 },
                      total: 0,
                      order_count: 0,
                      order_payed: 0,
                    };
                    userMap.set(user.id, userRecord);
                  }

                  const quantity = user.quantity;
                  userRecord.total += quantity;
                  userRecord.order_count += quantity;
                  if (user.payed) {
                    userRecord.order_payed += quantity;
                  }

                  if (shiftKey === "1-smena") {
                    userRecord.shift1.quantity += quantity;
                    companyTotals.shift1 += quantity;
                  } else if (shiftKey === "2-smena") {
                    userRecord.shift2.quantity += quantity;
                    companyTotals.shift2 += quantity;
                  } else if (shiftKey === "3-smena") {
                    userRecord.shift3.quantity += quantity;
                    companyTotals.shift3 += quantity;
                  } else if (shiftKey === "4-smena") {
                    userRecord.shift4.quantity += quantity;
                    companyTotals.shift4 += quantity;
                  }
                });
            });
          });

        let filteredUsers = Array.from(userMap.values());

        // Filter out users without valid ID
        filteredUsers = filteredUsers.filter(
          (user) => user.id && user.id !== null && user.id !== undefined
        );

        // Filter by order status
        if (this.orderStatus !== 0) {
          filteredUsers = filteredUsers.filter((user) => {
            const hasOrders = (user.order_count || 0) > 0;
            const hasTaken = (user.order_payed || 0) > 0;

            switch (this.orderStatus) {
              case 1: // Users who ordered
                return hasOrders;
              case 2: // Users who didn't order
                return !hasOrders;
              case 3: // Users who ordered but didn't take
                return hasOrders && !hasTaken;
              default:
                return true;
            }
          });
        }

        // Filter users by selected shifts and recalculate totals based on selected shifts
        filteredUsers = filteredUsers.map((user) => {
          const filteredUser = { ...user };

          // Reset shift values based on selected shifts
          if (!this.selectedShifts.includes(1)) {
            filteredUser.shift1 = { quantity: 0 };
          }
          if (!this.selectedShifts.includes(2)) {
            filteredUser.shift2 = { quantity: 0 };
          }
          if (!this.selectedShifts.includes(3)) {
            filteredUser.shift3 = { quantity: 0 };
          }
          if (!this.selectedShifts.includes(4)) {
            filteredUser.shift4 = { quantity: 0 };
          }

          // Recalculate total for filtered shifts
          filteredUser.total =
            filteredUser.shift1.quantity +
            filteredUser.shift2.quantity +
            filteredUser.shift3.quantity +
            filteredUser.shift4.quantity;

          return filteredUser;
        });

        // Calculate totals based on filtered shifts
        const filteredTotals = {
          shift1: 0,
          shift2: 0,
          shift3: 0,
          shift4: 0,
          total: 0,
        };

        filteredUsers.forEach((user) => {
          if (this.selectedShifts.includes(1)) {
            filteredTotals.shift1 += user.shift1.quantity;
          }
          if (this.selectedShifts.includes(2)) {
            filteredTotals.shift2 += user.shift2.quantity;
          }
          if (this.selectedShifts.includes(3)) {
            filteredTotals.shift3 += user.shift3.quantity;
          }
          if (this.selectedShifts.includes(4)) {
            filteredTotals.shift4 += user.shift4.quantity;
          }
          filteredTotals.total += user.total;
        });

        return {
          name: companyName,
          users: filteredUsers,
          totals: filteredTotals,
        };
      });
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies.map((company) => company.id);
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    restaurantId() {
      return this.$store.state.company?.restaurant_id || this.$store.state.company;
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", print: false },
        { text: "ID", value: "id" },
        { text: this.$t("external_code"), value: "external_code" },
        { text: this.$t("name"), value: "name" },
        { text: this.$t("last_name"), value: "lastname" },
      ];

      const shiftHeaders = [];

      // Dodaj header kolone samo za selektovane smene
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({ text: this.$t("shift_one"), value: "shift1.quantity" });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({ text: this.$t("shift_two"), value: "shift2.quantity" });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({ text: this.$t("shift_three"), value: "shift3.quantity" });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({ text: this.$t("shift_four"), value: "shift4.quantity" });
      }

      const endHeaders = [
        { text: this.$t("total"), value: "total" },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
        },
      ];

      return [...baseHeaders, ...shiftHeaders, ...endHeaders];
    },
    search() {
      return this.$store.state.report.filters.search;
    },
  },
  watch: {
    dates: {
      handler() {
        this.fetchUserlist();
      },
      deep: true,
    },
    selectedCompanies(newVal, oldVal) {
      this.fetchUserlist();
    },
    isPrinting(newVal, oldVal) {
      if (newVal) {
        this.printDocument({
          tables: this.companies.map((company) => {
            return {
              headers: this.headers.filter((h) => h.print !== false),
              items: this.customSearch(company.users, this.headers, this.search).map(
                (user) => ({
                  ...user,
                  company: company.name,
                })
              ),
              title: company.name + ' ' + this.subtitle,
              showCaption: true,
            };
          }),
          title: [this.$t("report"), this.$t("employees")].join(": "),
          hideDatetime: true,
          additionalStyles: `
              .print-table {
                page-break-after: always;
              }
              .print-table:last-child {
                page-break-after: auto;
              }
            `,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("employees")].join(" ") + ".xlsx",
          sheets: this.companies.map((company) => {
            return {
              name: company.name,
              headers: this.headers.filter((h) => h.print !== false),
              items: this.customSearch(company.users, this.headers, this.search),
              title: company.name,
              subtitle: this.subtitle,
            };
          }),
        });
      }
    },
  },
};
</script>
